import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

import { LoadingIcon } from '@/components/LoadingIcon'
import { OTC_WIRE_INSTRUCTIONS_PDF_URL } from '@/constants/otc'
import { useWireInstructions } from '@/hooks/useOtc'
import {
  trackOtcWireInstructionsDownloaded,
  trackOtcWireInstructionsLoadFailed,
  trackOtcWireInstructionsToggled,
} from '@/utils/analytics'
import type { OtcWireInstructions } from '@/requests/otc'

type WireInstructionsProps = {
  amount: number | null | undefined
  currency: string
}

type WireRow = { label: string; value: string }
type WireSection = { title: string; rows: WireRow[] }

export const formatWireAmount = (amount: number | null | undefined): string =>
  amount == null
    ? '—'
    : amount.toLocaleString('en-CA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

const buildSections = (
  t: (key: string) => string,
  details: OtcWireInstructions
): WireSection[] => [
  {
    title: t('otc.wire.bankDetails'),
    rows: [
      { label: t('otc.wire.bankName'), value: details.bank.name },
      { label: t('otc.wire.bankAddress'), value: details.bank.address },
      { label: t('otc.wire.swiftCode'), value: details.bank.swiftCode },
    ],
  },
  {
    title: t('otc.wire.recipient'),
    rows: [
      {
        label: t('otc.wire.beneficiaryName'),
        value: details.recipient.beneficiaryName,
      },
      {
        label: t('otc.wire.beneficiaryAddress'),
        value: details.recipient.beneficiaryAddress,
      },
      {
        label: t('otc.wire.institution'),
        value: details.recipient.institutionNumber,
      },
      {
        label: t('otc.wire.transit'),
        value: details.recipient.transitNumber,
      },
      {
        label: t('otc.wire.account'),
        value: details.recipient.accountNumber,
      },
    ],
  },
]

const shellClassName = 'rounded border border-gray-200 text-sm'

const WireInstructionsComponent = ({
  amount,
  currency,
}: WireInstructionsProps) => {
  const { t } = useTranslation()
  const { data: details, isLoading, isError } = useWireInstructions()
  const [open, setOpen] = useState(true)

  const hasFailedToLoad = !isLoading && (isError || !details)
  useEffect(() => {
    if (!hasFailedToLoad) return
    trackOtcWireInstructionsLoadFailed()
  }, [hasFailedToLoad])

  const handleToggle = useCallback(() => {
    const nextOpen = !open
    trackOtcWireInstructionsToggled(nextOpen)
    setOpen(nextOpen)
  }, [open])

  if (isLoading) {
    return (
      <div
        className={`${shellClassName} flex justify-center p-4`}
        data-testid="container-wire-instructions-loading"
      >
        <LoadingIcon />
      </div>
    )
  }

  if (isError || !details) {
    return (
      <div
        className={`${shellClassName} p-4 text-gray-500`}
        data-testid="container-wire-instructions-error"
      >
        {t('otc.wire.error')}
      </div>
    )
  }

  const sections = buildSections(t, details)

  return (
    <div
      className={shellClassName}
      data-testid="container-wire-instructions-details"
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 p-4 text-left font-medium"
        onClick={handleToggle}
        aria-expanded={open}
        aria-controls="wire-instructions-body"
        data-testid="btn-wire-instructions-toggle"
      >
        <span>{t('otc.wire.title')}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div id="wire-instructions-body" className="px-4 pb-4">
          <div className="mb-4 flex flex-col">
            <span className="text-xs text-gray-500">
              {t('otc.wire.amount')}
            </span>
            <span className="font-medium" data-testid="text-wire-amount">
              {formatWireAmount(amount)} {currency}
            </span>
          </div>

          {sections.map((section) => (
            <div key={section.title} className="mb-4 last:mb-0">
              <div className="mb-2 text-xs uppercase tracking-wide text-gray-400">
                {section.title}
              </div>
              <dl className="flex flex-col gap-2">
                {section.rows.map((row) => (
                  <div key={row.label} className="flex flex-col">
                    <dt className="text-xs text-gray-500">{row.label}</dt>
                    <dd className="font-medium break-words">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}

          <a
            href={OTC_WIRE_INSTRUCTIONS_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="mt-4 inline-block text-sm underline"
            onClick={trackOtcWireInstructionsDownloaded}
            data-testid="link-wire-download"
          >
            {t('otc.wire.download')}
          </a>
        </div>
      )}
    </div>
  )
}

export const WireInstructions = React.memo(WireInstructionsComponent)
